import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";

import { AuthShell, authButton } from "@/components/AuthShell";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { loginPathFor } from "@/lib/password";
import { useSession, type UserRole } from "@/lib/session";

/**
 * Confirmar a troca de e-mail — o link chega no e-mail ATUAL da conta.
 *
 * Pede um clique em vez de trocar ao abrir: prévias de link (antivírus,
 * WhatsApp) abrem a URL sozinhas, e isso não pode confirmar nada.
 */
export const Route = createFileRoute("/confirmar-email")({
  head: () => ({ meta: [{ title: "Confirmar e-mail — MonteBox" }] }),
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search["token"] === "string" ? { token: search["token"] } : {},
  component: ConfirmEmail,
});

interface EmailChangeInfo {
  name: string;
  currentEmail: string;
  newEmail: string;
}

function ConfirmEmail() {
  const { token = "" } = Route.useSearch();
  const { profile, refreshProfile } = useSession();
  const info = useQuery({
    queryKey: ["email-change", token],
    queryFn: () =>
      api.get<EmailChangeInfo>(`/auth/email-change/${encodeURIComponent(token)}`, {
        auth: false,
      }),
    enabled: token.length > 0,
    retry: false,
  });
  const confirm = useMutation({
    mutationFn: () =>
      api.post<{ email: string; role: UserRole }>(
        "/auth/email-change/confirm",
        { token },
        { auth: false },
      ),
    // Quem está logado vê o e-mail novo na hora em "Meus dados".
    onSuccess: () => (profile ? refreshProfile() : undefined),
  });

  if (confirm.data) {
    return (
      <AuthShell
        title="E-mail alterado"
        subtitle={
          <>
            A partir de agora, entre com{" "}
            <strong className="text-mb-ink">{confirm.data.email}</strong>.
          </>
        }
      >
        <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-emerald-700">
          <CheckCircle2 className="h-5 w-5" />
          Troca confirmada.
        </div>
        <Link
          to={profile ? "/minha-conta/dados" : loginPathFor(confirm.data.role)}
          className={authButton}
        >
          {profile ? "Voltar para Meus dados" : "Entrar"}
        </Link>
      </AuthShell>
    );
  }

  if (!token || info.isError) {
    return (
      <AuthShell
        title="Link inválido"
        subtitle="Este link expirou, foi cancelado ou já foi usado. Peça a troca de novo em Meus dados."
      >
        <Link to="/minha-conta/dados" className={authButton}>
          Ir para Meus dados
        </Link>
      </AuthShell>
    );
  }

  if (!info.data) {
    return (
      <AuthShell title="Conferindo o link…">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-mb-muted" />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Confirmar troca de e-mail"
      subtitle={`Olá, ${info.data.name.split(" ")[0]}! Confira antes de confirmar.`}
    >
      <dl className="grid gap-3 rounded-mb-field bg-mb-surface p-4 text-sm">
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-mb-muted">
            E-mail atual
          </dt>
          <dd className="font-semibold text-mb-ink [overflow-wrap:anywhere]">
            {info.data.currentEmail}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-mb-muted">
            Novo e-mail (e novo login)
          </dt>
          <dd className="font-semibold text-mb-navy [overflow-wrap:anywhere]">
            {info.data.newEmail}
          </dd>
        </div>
      </dl>
      {confirm.isError && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {errorMessage(confirm.error)}
        </p>
      )}
      <button
        type="button"
        onClick={() => confirm.mutate()}
        disabled={confirm.isPending}
        className={`${authButton} mt-6`}
      >
        {confirm.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
        Confirmar a troca
      </button>
      <p className="mt-3 text-center text-xs text-mb-muted">
        Não foi você? Não confirme — e troque a sua senha em Meus dados.
      </p>
    </AuthShell>
  );
}
